"use client"
import React, { useState, useMemo, useCallback } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  Avatar,
  Container,
  Grid,
  Typography,
  Box,
  Card,
  Button,
  Stack,
  Chip,
  Divider,
  Tooltip,
} from "@mui/material"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Sector
} from "recharts"
import { PieChart } from '@mui/x-charts/PieChart'
import { useEffectAsync } from "../reactHelper"
import { useTheme } from "@mui/material/styles"
import PageLayout from "../common/components/PageLayout"
import SettingsMenu from "./components/SettingsMenu"
import DataTable from "../common/components/Datatable"
import { makeStyles } from "tss-react/mui"
import { sessionActions } from "../store"
import LogoImage from "../login/LogoImage"
import { nativePostMessage } from "../common/components/NativeInterface"
import { jsPDF } from "jspdf"
import "jspdf-autotable"

// Icons
import Diversity3Icon from "@mui/icons-material/Diversity3"
import GroupIcon from "@mui/icons-material/Group"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import DevicesIcon from "@mui/icons-material/Devices"
import TerrainIcon from "@mui/icons-material/Terrain"
import ErrorIcon from "@mui/icons-material/Error"
import HelpIcon from "@mui/icons-material/Help"
import AllInclusiveIcon from "@mui/icons-material/AllInclusive"
import LockIcon from "@mui/icons-material/Lock"
import LockOpenIcon from '@mui/icons-material/LockOpen'

// Utilities and Hooks
import { useTranslation } from "../common/components/LocalizationProvider"
import { useDeviceReadonly, useAdministrator, useManager } from "../common/util/permissions"
import { formatBoolean, formatDate, getStatusText, formatAlarm } from "../common/util/formatter"
import { mapIconKey, mapIcons } from "../map/core/preloadImages"
import { usePreference } from "../common/util/preferences"
import useSettingsStyles from "./common/useSettingsStyles"

const useStyles = makeStyles()((theme) => ({
  avatar: {
    display: "inline-block",
    border: "2px solid white",
    "&:not(:first-of-type)": {
      marginLeft: `-${theme.spacing(1)}`,
    },
  },
  button: {
    margin: theme.spacing(1),
  },
  statusIcon: {
    fontSize: '1rem',
  },
  error: {
    color: '#f44336',
  },
}))

const PrintHeader = () => {
  const t = useTranslation()
  const currentUser = useSelector((state) => state.session.user)

  const printStyles = {
    container: {
      padding: 10,
      color: "#000",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
    },
    title: {
      fontSize: 24,
      margin: 0,
      color: "#000",
    },
    infoSection: {
      marginTop: 10,
      fontSize: 14,
      color: "#333",
    },
    infoLabel: {
      fontWeight: "bold",
      color: "#000",
    },
  }

  return (
    <div style={printStyles.container}>
      <div style={printStyles.header}>
        <LogoImage
          style={{ height: 60 }}
          onLoad={async () => {
            requestAnimationFrame(() =>
              requestAnimationFrame(() => {
                window.print()
                nativePostMessage(`print|${t("dashboardMenuText")}.pdf`)
              }),
            )
          }}
          alt={t("companyLogo")}
        />
        <h1 style={printStyles.title}>{t("dashboardMenuText")}</h1>
      </div>
      <div style={printStyles.infoSection}>
        <div>
          <strong style={printStyles.infoLabel}>{t("shared.user")}:</strong> {currentUser?.name || ""}
        </div>
        <div>
          <strong style={printStyles.infoLabel}>{t("reportDate")}:</strong> {new Date().toLocaleString()}
        </div>
      </div>
    </div>
  )
}

const CustomPieLegend = ({ data, theme }) => {
  const t = useTranslation()
  const total = data.reduce((sum, item) => sum + item.value, 0)

  return (
    <Grid container spacing={1} justifyContent="center" sx={{ mt: 2 }}>
      {data.map((item, index) => {
        const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0
        const color = [
          theme.palette.success.main,
          theme.palette.error.main,
          theme.palette.warning.main
        ][index]

        return (
          <Grid item key={index} xs={12} sm={4} sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{
                width: 16,
                height: 16,
                bgcolor: color,
                mr: 1,
                borderRadius: '4px'
              }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {item.name}:
              </Typography>
              <Typography variant="body2" sx={{ ml: 0.5 }}>
                {item.value} ({percentage}%)
              </Typography>
            </Box>
          </Grid>
        )
      })}
      <Grid item xs={12} sx={{ textAlign: 'center', ml: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t('deviceStatusTotal')}: {total}
        </Typography>
      </Grid>
    </Grid>
  )
}

const CustomTooltip = ({ active, payload, label, theme }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[3],
      }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Typography
            key={index}
            variant="body2"
            style={{ color: entry.color, display: 'block' }}
          >
            {`${entry.name}: ${entry.value}`}
          </Typography>
        ))}
      </div>
    )
  }
  return null
}

const DashboardPage = () => {
  const theme = useTheme()
  const t = useTranslation()
  const dispatch = useDispatch()
  const { classes } = useStyles()
  const settingsClasses = useSettingsStyles()

  const admin = useAdministrator()
  const manager = useManager()
  const deviceReadonly = useDeviceReadonly()

  const userLimitCommands = usePreference("limitCommands")
  const userDeviceReadonly = usePreference("deviceReadonly")
  const userReadonly = usePreference("readonly")

  const positions = useSelector((state) => Object.values(state.positions.items || {}))
  const groups = useSelector((state) => Object.values(state.groups.items || {}))
  const geofences = useSelector((state) => Object.values(state.geofences.items || {}))
  const currentUser = useSelector((state) => state.session.user)
  const printing = useSelector((state) => state.session.printing)

  const [devices, setDevices] = useState([])
  const [users, setUsers] = useState([currentUser])
  const [deviceStatus, setDeviceStatus] = useState("")
  const [loading, setLoading] = useState(false)
  const [activeUsersStat, setActiveUsersStat] = useState([])
  const [activeDevicesStat, setActiveDevicesStat] = useState([])
  const [messagesReceivedStat, setMessagesReceivedStat] = useState([])
  const [statistics, setItemsStatistics] = useState({})
  const [deviceStatusChartData, setDeviceStatusChartData] = useState([])
  const [activeIndex, setActiveIndex] = useState(null)

  const filteredDevices = useMemo(
    () => devices.filter((x) => (deviceStatus === "" ? x.status !== "never" : x.status.includes(deviceStatus))),
    [devices, deviceStatus],
  )

  const statCards = useMemo(
    () => [
      {
        icon: <GroupIcon color="primary" fontSize="medium" />,
        label: t("settingsUsers"),
        value: users.length,
      },
      {
        icon: <DevicesIcon color="primary" fontSize="medium" />,
        label: t("deviceTitle"),
        value: devices.length,
      },
      {
        icon: <Diversity3Icon color="primary" fontSize="medium" />,
        label: t("settingsGroups"),
        value: groups.length,
      },
      {
        icon: <TerrainIcon color="primary" fontSize="medium" />,
        label: t("sharedGeofences"),
        value: geofences.length,
      },
    ],
    [groups.length, users.length, devices.length, geofences.length, t],
  )

  const columns = useMemo(
    () => [
      {
        field: "photo",
        headerName: "",
        width: 60,
        disableColumnMenu: true,
        sortable: false,
        renderCell: (params) => (
          <Avatar alt={params.row.name} src={getDeviceIcon(params.row)} sx={{ width: 32, height: 32 }} />
        ),
      },
      { field: "name", headerName: t("sharedName"), flex: 1, sortable: false },
      { field: "uniqueId", headerName: t("deviceIdentifier"), flex: 1 },
      { field: "contact", headerName: t("sharedDevicePlate"), flex: 1 },
      { field: "phone", headerName: t("sharedPhone"), flex: 1 },
      { field: "model", headerName: t("deviceModel"), flex: 1 },
      {
        field: "deviceLastUpdate",
        headerName: t("deviceLastUpdate"),
        flex: 1,
        renderCell: (params) => (params.row.lastUpdate ? formatDate(params.row.lastUpdate) : ""),
      },
      {
        field: "ignition",
        headerName: t("positionIgnition"),
        flex: 1,
        renderCell: (params) => {
          const position = positions.find((p) => p.id === params.row.positionId)
          if (!position || !position.attributes) {
            return <Chip label={t("sharedNoData")} size="small" />
          }
          return position.attributes.ignition !== undefined ? (
            <Chip
              label={formatBoolean(position.attributes.ignition, t)}
              color={position.attributes.ignition ? "success" : "error"}
              size="small"
            />
          ) : <Chip label={t("sharedUnknown")} size="small" />
        },
      },
      {
        field: "eventAlarm",
        headerName: t("eventAlarm"),
        flex: 2,
        renderCell: (params) => {
          const position = positions.find((p) => p.id === params.row.positionId)
          if (!position || !position.attributes) {
            return <Chip label={t("sharedNoData")} size="small" />
          }
          return position.attributes.alarm ? (
            <Chip
              label={formatAlarm(position.attributes.alarm, t)}
              color="success"
              size="small"
            />
          ) : null
        },
      },
      {
        field: "positionBlocked",
        headerName: t("positionBlocked"),
        flex: 1,
        renderCell: (params) => {
          const position = positions.find((p) => p.id === params.row.positionId)
          if (!position) {
            return <Chip label={t("sharedNoData")} color="default" size="small" />
          }
          if (!position.attributes) {
            return <Chip label={t("sharedNoData")} color="warning" size="small" />
          }
          const blocked = position.attributes.blocked
          if (typeof blocked === "boolean") {
            return (
              <Tooltip title={blocked ? t("positionBlocked") : t("positionUnlocked")}>
                <Chip
                  icon={blocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                  label={blocked ? t("sharedYes") : t("sharedNo")}
                  color={blocked ? "error" : "success"}
                  size="small"
                />
              </Tooltip>
            )
          }
          return <Chip label={t("eventDeviceUnknown")} color="default" size="small" />
        },
      },
      {
        field: "deviceStatus",
        headerName: t("deviceStatus"),
        flex: 1,
        renderCell: (params) => {
          const status = params.row.status?.toLowerCase() || "unknown"
          const color = status === "online" ? "success" : status === "offline" ? "error" : "warning"
          return <Chip label={getStatusText(params.row.status || "unknown", t)} color={color} size="small" />
        },
      },
    ],
    [positions, t]
  )

  const getDeviceIcon = useCallback((item) => {
    if (item.photo && item.photo.length > 0 && item.photo.length < 30) {
      return `/photos/devices/${item.photo}`
    }
    return mapIcons[mapIconKey(item.category)] || "/placeholder.svg"
  }, [])

  const getAvatar = useCallback((item) => {
    let filephoto = "default.png"
    if (item.photo) {
      if (item.photo.preview) {
        return item.photo
      } else if (item.photo.length > 0 && item.photo.length < 30) {
        filephoto = item.photo
      }
    }
    return `./photos/users/${filephoto}`
  }, [])

  const fetchDevicesAndUsers = useCallback(async () => {
    setLoading(true)
    try {
      const devicesResponse = await fetch("/api/devices")
      if (devicesResponse.ok) {
        const deviceData = await devicesResponse.json()
        setDevices(deviceData)
        setDeviceStatusChartData([
          { id: 0, name: t("deviceStatusOnline"), value: deviceData.filter((d) => d.status === "online").length },
          { id: 1, name: t("deviceStatusOffline"), value: deviceData.filter((d) => d.status === "offline").length },
          { id: 2, name: t("deviceStatusUnknown"), value: deviceData.filter((d) => d.status === "unknown").length },
        ])
      }

      if (admin || manager) {
        const usersResponse = await fetch("/api/users")
        if (usersResponse.ok) {
          setUsers(await usersResponse.json())
        }
      }
    } catch (error) {
      console.error("Failed to fetch data:", error)
    } finally {
      setLoading(false)
    }
  }, [admin, manager, t])

  const fetchStatistics = useCallback(async () => {
    try {
      const selectedFrom = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      const selectedTo = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)

      const query = new URLSearchParams({
        from: selectedFrom.toISOString(),
        to: selectedTo.toISOString(),
      })

      const response = await fetch(`/api/statistics?${query.toString()}`, {
        Accept: "application/json",
      })

      if (response.ok) {
        const result = await response.json()
        const dailyData = result.map((entry) => {
          const date = new Date(entry.captureTime)
          const day = date.getDate().toString().padStart(2, "0")
          const month = (date.getMonth() + 1).toString().padStart(2, "0")
          return {
            name: `${day}/${month}`,
            activeUsers: entry.activeUsers,
            activeDevices: entry.activeDevices,
            messagesReceived: entry.messagesReceived,
          }
        })

        setActiveUsersStat(dailyData.map((item) => ({ name: item.name, users: item.activeUsers })))
        setActiveDevicesStat(dailyData.map((item) => ({ name: item.name, devices: item.activeDevices })))
        setMessagesReceivedStat(dailyData.map((item) => ({ name: item.name, messages: item.messagesReceived })))

        let activeUsers = 0
        let activeDevices = 0
        let messagesReceived = 0

        for (const stats of result) {
          activeUsers += stats.activeUsers
          activeDevices += stats.activeDevices
          messagesReceived += stats.messagesReceived
        }

        setItemsStatistics({
          activeUsers: Math.round(activeUsers / result.length),
          activeDevices: Math.round(activeDevices / result.length),
          messagesReceived,
        })
      }
    } catch (error) {
      console.error("Failed to fetch statistics:", error)
    }
  }, [])

  useEffectAsync(async () => {
    await fetchDevicesAndUsers()
    await fetchStatistics()
  }, [fetchDevicesAndUsers, fetchStatistics])

  if (printing) {
    return (
      <div style={{ color: "black", backgroundColor: "white" }}>
        <PrintHeader />
        <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ color: "black", fontWeight: "bold", textAlign: "center" }}>
            {t("dashboardMenuText")}
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {statCards.map((stat, index) => (
              <Grid key={index} sx={{ width: { xs: "100%", sm: "50%", md: "25%" } }}>
                <Card className={settingsClasses.statCard}>
                  <Box className={settingsClasses.statIcon}>{stat.icon}</Box>
                  <Box className={settingsClasses.statTextContainer}>
                    <Typography variant="body2">{stat.label}</Typography>
                    <Typography className={settingsClasses.statValue}>{stat.value}</Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {activeUsersStat.length > 0 && (
            <Card elevation={0} sx={{ p: 3, borderRadius: 3, marginBottom: "20px", border: "1px solid #ddd" }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "black", marginBottom: "10px" }}>
                {t("statisticsActiveUsers")}
              </Typography>
              <div style={{ height: "300px", width: "100%" }}>
                <ResponsiveContainer>
                  <LineChart data={activeUsersStat}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Line type="monotone" dataKey="users" stroke="#82ca9d" strokeWidth={3} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 8px)" } }}>
            <Card elevation={1} sx={{ p: 2, height: "100%" }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                {t("devicesAndState")}
              </Typography>
              <Box sx={{ height: 300 }}>
                {deviceStatusChartData.length > 0 ? (
                  <>
                    <PieChart
                      series={[{
                        data: deviceStatusChartData,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: {
                          innerRadius: 30,
                          additionalRadius: -30,
                          color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]
                        },
                      }]}
                      height={280}
                      slotProps={{
                        legend: { hidden: true },
                        tooltip: {
                          formatter: (params) => {
                            const total = deviceStatusChartData.reduce((sum, item) => sum + item.value, 0)
                            const percentage = total > 0 ? ((params.value / total) * 100).toFixed(1) : 0
                            return [
                              `${params.name}: ${params.value} (${percentage}%)`,
                              t('sharedTotal') + ': ' + total
                            ]
                          },
                        },
                      }}
                      colors={[theme.palette.success.main, theme.palette.error.main, theme.palette.warning.main]}
                    />
                    <CustomPieLegend data={deviceStatusChartData} theme={theme} />
                  </>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography variant="body1">{t("sharedLoading")}</Typography>
                  </Box>
                )}
              </Box>
            </Card>
          </Box>

          <Typography variant="h5" gutterBottom sx={{ color: "black", fontWeight: "bold", marginTop: "20px", marginBottom: "10px" }}>
            {t("deviceTitle")}
          </Typography>

          <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #ddd" }}>
            <thead>
              <tr>
                {columns.filter((col) => col.field !== "photo").map((column) => (
                  <th key={column.field} style={{ padding: "8px", backgroundColor: "#f2f2f2", color: "black", fontWeight: "bold", border: "1px solid #ddd", textAlign: "left" }}>
                    {column.headerName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredDevices.map((device) => (
                <tr key={device.id}>
                  {columns.filter((col) => col.field !== "photo").map((column) => {
                    let cellContent
                    if (column.renderCell) {
                      const rendered = column.renderCell({ row: device })
                      cellContent = React.isValidElement(rendered) ? (rendered.type === Chip ? rendered.props.label : "-") : rendered
                    } else {
                      cellContent = device[column.field] || "-"
                    }
                    return (
                      <td key={`${device.id}-${column.field}`} style={{ padding: "8px", border: "1px solid #ddd", color: "black" }}>
                        {cellContent}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Container>
      </div>
    )
  }

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={["settingsTitle", "dashboard"]}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2, height: "100%" }}>
        <Card sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.5, minHeight: 56, flexWrap: "wrap", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 200 }}>
            <Avatar src={getAvatar(currentUser)} sx={{ width: 40, height: 40, mr: 1 }} />
            <Typography variant="body1" fontWeight="bold" noWrap>
              {currentUser.name}
            </Typography>
          </Box>

          <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} spacing={1} useFlexGap sx={{ flexGrow: 1, flexWrap: "wrap", justifyContent: "center", minWidth: 250, maxWidth: "100%" }}>
            <Typography variant="body2" noWrap>
              <strong>{t("userLimitCommands")}:</strong> {formatBoolean(userLimitCommands, t)}
            </Typography>
            <Typography variant="body2" noWrap>
              <strong>{t("userDeviceReadonly")}:</strong> {formatBoolean(userDeviceReadonly, t)}
            </Typography>
            <Typography variant="body2" noWrap>
              <strong>{t("serverReadonly")}:</strong> {formatBoolean(userReadonly, t)}
            </Typography>
          </Stack>
        </Card>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 2, height: "auto" }}>
          {statCards.map((stat, index) => (
            <Card key={index} sx={{ p: 4, display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", minHeight: "auto", height: 80, boxShadow: 1, border: "1px solid", borderColor: "divider" }}>
              <Box sx={{ color: "primary.contrastText", p: 1.8, borderRadius: "50%", mr: 3, display: "flex", width: 48, height: 48, alignItems: "center", justifyContent: "center" }}>
                {React.cloneElement(stat.icon, { fontSize: "medium", sx: { fontSize: "60px" } })}
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, overflow: "hidden" }}>
                <Typography variant="body2" fontWeight={500} noWrap>{stat.label}</Typography>
                <Typography variant="h4" fontWeight={700}>{stat.value}</Typography>
              </Box>
            </Card>
          ))}
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 8px)" }, minWidth: 0 }}>
            <Card elevation={1} sx={{ p: 2, height: "100%" }}>
              <Typography variant="subtitle1" fontWeight="bold">{t("statisticsActiveUsers")}</Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={activeUsersStat}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip content={<CustomTooltip theme={theme} />} />
                    <Legend />
                    <Line type="monotone" dataKey="users" name={t("statisticsActiveUsers")} stroke="#2e7d32" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Card>
          </Box>

          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 8px)" } }}>
            <Card elevation={1} sx={{ p: 2, height: "100%" }}>
              <Typography variant="subtitle1" fontWeight="bold">{t("statisticsMessagesReceived")}</Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={messagesReceivedStat}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip content={<CustomTooltip theme={theme} />} />
                    <Legend />
                    <Line type="monotone" dataKey="messages" name={t("statisticsMessagesReceived")} stroke="#9c27b0" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6, fill: "#9c27b0" }} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Card>
          </Box>

          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 8px)" } }}>
            <Card elevation={1} sx={{ p: 2, height: "100%" }}>
              <Typography variant="subtitle1" fontWeight="bold">{t("statisticsActiveDevices")}</Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={activeDevicesStat}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip content={<CustomTooltip theme={theme} />} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="devices"
                      name={t("statisticsActiveDevices")}
                      stroke="#1976d2"
                      strokeWidth={2.5}
                      dot={{ r: 4, fill: "#1976d2", stroke: "#fff", strokeWidth: 1 }}
                      activeDot={{ r: 6, fill: "#1976d2", stroke: "#fff", strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Card>
          </Box>

          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 8px)" } }}>
            <Card elevation={1} sx={{ p: 2, height: "100%" }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                {t("devicesAndState")}
              </Typography>
              <Box sx={{ height: 300 }}>
                {deviceStatusChartData.length > 0 ? (
                  <>
                    <PieChart
                      series={[{
                        data: deviceStatusChartData,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: {
                          innerRadius: 30,
                          additionalRadius: -30,
                          color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]
                        },
                      }]}
                      height={280}
                      slotProps={{
                        legend: { hidden: true },
                        tooltip: {
                          formatter: (params) => {
                            const total = deviceStatusChartData.reduce((sum, item) => sum + item.value, 0)
                            const percentage = total > 0 ? ((params.value / total) * 100).toFixed(1) : 0
                            return [
                              `${params.name}: ${params.value} (${percentage}%)`,
                              t('sharedTotal') + ': ' + total
                            ]
                          },
                        },
                      }}
                      colors={[theme.palette.success.main, theme.palette.error.main, theme.palette.warning.main]}
                    />
                    <CustomPieLegend data={deviceStatusChartData} theme={theme} />
                  </>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography variant="body1">{t("sharedLoading")}</Typography>
                  </Box>
                )}
              </Box>
            </Card>
          </Box>

          <Card elevation={1} sx={{ mt: 3, width: "100%" }}>
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("deviceTitle")}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Button
                    onClick={() => setDeviceStatus("online")}
                    variant={deviceStatus === "online" ? "contained" : "outlined"}
                    size="small"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                  >
                    {t("deviceStatusOnline")}
                  </Button>
                  <Button
                    onClick={() => setDeviceStatus("offline")}
                    variant={deviceStatus === "offline" ? "contained" : "outlined"}
                    size="small"
                    color="error"
                    startIcon={<ErrorIcon />}
                  >
                    {t("deviceStatusOffline")}
                  </Button>
                  <Button
                    onClick={() => setDeviceStatus("unknown")}
                    variant={deviceStatus === "unknown" ? "contained" : "outlined"}
                    size="small"
                    color="warning"
                    startIcon={<HelpIcon />}
                  >
                    {t("deviceStatusUnknown")}
                  </Button>
                  <Button
                    onClick={() => setDeviceStatus("")}
                    variant={deviceStatus === "" ? "contained" : "outlined"}
                    size="small"
                    color="primary"
                    startIcon={<AllInclusiveIcon />}
                  >
                    {t("notificationAlways")}
                  </Button>
                </Stack>
              </Box>
              <Box sx={{ flex: 1, height: "600px" }}>
                <DataTable
                  tableName="devices"
                  columns={columns}
                  rows={filteredDevices}
                  loading={loading}
                  filterable={true}
                  enablePdfExport={manager}
                  enableXlsxExport={manager}
                  pageSizeOptions={[5, 10, 25, 50, 100]}
                  getRowId={(row) => row.id}
                  sx={{
                    height: "100%",
                    "& .MuiDataGrid-root": { border: "none" },
                  }}
                  iconColor="action"
                />
              </Box>
            </Box>
          </Card>
        </Box>
      </Box>
    </PageLayout>
  )
}

export default React.memo(DashboardPage)